import { TFunction } from 'i18next'

import { EMPTY_VALUE } from '@/constants'
import {
  DisplayColumnFields,
  DisplayFieldType,
  PersonDisplay,
} from '@/models/features/display-column.models'
import { UserRoleEnum } from '@/models/features/user.models'

import {
  COLUMN_WIDTH_REM,
  MAX_PREVIEW_SAMPLES,
  PERSON_FIELDS_BY_TYPE,
  RESIDENT_ONLY_FIELDS,
} from './person-column.constants'
import { Person, PersonCellContent, PersonFieldKey, PersonTag } from './person-column.types'

type PersonColumnT = TFunction<['person-column']>

export const isFieldEnabled = (fields: DisplayColumnFields, field: PersonFieldKey) =>
  field === 'name' ? true : fields[field]

export const setFieldEnabled = (
  fields: DisplayColumnFields,
  field: PersonFieldKey,
  enabled: boolean,
): DisplayColumnFields =>
  field === 'name'
    ? { ...fields, firstName: true, lastName: true }
    : { ...fields, [field]: enabled }

export const areFieldsEqual = (
  fieldType: DisplayFieldType,
  a: DisplayColumnFields,
  b: DisplayColumnFields,
) =>
  PERSON_FIELDS_BY_TYPE[fieldType].every(
    (field) => isFieldEnabled(a, field) === isFieldEnabled(b, field),
  )

// Note: whether a field can show anything for this row - a Mixed column's staff rows have no
// unit / building / address.
export const fieldAppliesTo = (field: PersonFieldKey, person: Person) =>
  person.kind === 'resident' || !RESIDENT_ONLY_FIELDS.includes(field)

export const getPersonName = (person: Person) =>
  [person.firstName, person.lastName].filter(Boolean).join(' ').trim()

export const getRoleLabel = (t: PersonColumnT, person: Person) => {
  if (person.kind === 'guest') return t('person-column:roles.Guest')
  // Note: a resident's role is "Client" (or missing) - shown as "Resident"
  const role = person.kind === 'resident' ? UserRoleEnum.enum.Client : person.role
  const parsed = UserRoleEnum.safeParse(role)
  if (!parsed.success) return role ?? ''
  return t(`person-column:roles.${parsed.data}`)
}

const toText = (value: string | number | null | undefined) =>
  value === null || value === undefined ? '' : String(value).trim()

// Note: the value a field shows for this row, '' when the person doesn't have it. A staff
// member's missing phone is the one exception - shown as "-" so it's visibly accounted for.
export const getFieldValue = (t: PersonColumnT, field: PersonFieldKey, person: Person) => {
  if (!fieldAppliesTo(field, person)) return ''

  switch (field) {
    case 'avatar':
      return toText(person.avatar)
    case 'name':
      return getPersonName(person)
    case 'role':
      return getRoleLabel(t, person)
    case 'phone':
      return toText(person.phone) || (person.kind === 'staff' ? EMPTY_VALUE : '')
    default:
      return toText(person[field])
  }
}

// Note: the person a table cell shows - identity (kind, profile link) from the raw person, every
// value from the BE's `<column>Display`, which only holds the enabled fields. A row without one
// (an older BE response) keeps its raw values; the name falls back to them too, as it's always on.
export const applyPersonDisplay = (
  person: Person,
  display: PersonDisplay | null | undefined,
): Person => {
  if (!display) return person
  const text = (value: string | number | null | undefined) =>
    value === null || value === undefined ? null : String(value)

  return {
    apartment: display.apartment ?? null,
    avatar: text(display.avatar),
    buildingNumber: display.buildingNumber ?? null,
    clientId: person.clientId,
    email: text(display.email),
    firstName: text(display.firstName) ?? person.firstName,
    kind: person.kind,
    lastName: text(display.lastName) ?? person.lastName,
    phone: display.phone ?? null,
    role: text(display.role),
    street: text(display.street),
  }
}

const TAG_FIELDS: PersonFieldKey[] = ['role', 'apartment', 'buildingNumber']

// Note: the fields a cell shows, in render order
const getEnabledFields = (fields: DisplayColumnFields, fieldType: DisplayFieldType) =>
  PERSON_FIELDS_BY_TYPE[fieldType].filter((field) => isFieldEnabled(fields, field))

export const getPersonCellContent = (
  t: PersonColumnT,
  person: Person,
  fields: DisplayColumnFields,
  fieldType: DisplayFieldType,
): PersonCellContent => {
  const enabled = getEnabledFields(fields, fieldType)

  const tags = enabled
    .filter((field) => TAG_FIELDS.includes(field))
    .map((field) => ({ field, value: getFieldValue(t, field, person) }) as PersonTag)
    .filter((tag) => !!tag.value)

  const details = enabled
    .filter((field) => !TAG_FIELDS.includes(field) && field !== 'avatar' && field !== 'name')
    .map((field) => ({ field, value: getFieldValue(t, field, person) }))
    .filter((detail) => !!detail.value)

  return {
    details,
    name: getPersonName(person) || EMPTY_VALUE,
    showAvatar: fields.avatar,
    tags,
  }
}

export const getPersonColumnWidth = (
  fields: DisplayColumnFields,
  fieldType: DisplayFieldType,
  { hasLeadingDetail = false } = {},
) => {
  const enabled = getEnabledFields(fields, fieldType)
  const tagsWidth = enabled.reduce((sum, field) => {
    if (field === 'role') return sum + COLUMN_WIDTH_REM.roleTag
    if (field === 'apartment' || field === 'buildingNumber') return sum + COLUMN_WIDTH_REM.tag
    return sum
  }, 0)
  // Note: the second line - the page's leading detail (e.g. a date), then the " · " details
  const secondLine = [
    ...(hasLeadingDetail ? [COLUMN_WIDTH_REM.leadingDetail] : []),
    ...enabled
      .filter((field) => field === 'email' || field === 'phone' || field === 'street')
      .map((field) => COLUMN_WIDTH_REM[field]),
  ]
  const secondLineWidth =
    secondLine.reduce((sum, width) => sum + width, 0) +
    Math.max(0, secondLine.length - 1) * COLUMN_WIDTH_REM.separator

  const width =
    (fields.avatar ? COLUMN_WIDTH_REM.avatar : 0) +
    COLUMN_WIDTH_REM.cellPadding +
    Math.max(COLUMN_WIDTH_REM.name + tagsWidth, secondLineWidth)

  return `${Math.round(Math.min(COLUMN_WIDTH_REM.max, Math.max(COLUMN_WIDTH_REM.min, width)) * 10) / 10}rem`
}

const hasMissingDetail = (t: PersonColumnT, person: Person, fieldType: DisplayFieldType) =>
  PERSON_FIELDS_BY_TYPE[fieldType].some(
    (field) =>
      fieldAppliesTo(field, person) &&
      ['', EMPTY_VALUE].includes(getFieldValue(t, field, person)) &&
      field !== 'avatar',
  )

// Note: up to 4 distinct people from the current page for the modal preview - the first row, plus (where
// the page has them) one with missing details and, for Mixed columns, a resident and a non-resident row.
export const pickPreviewSamples = <T>(
  t: PersonColumnT,
  rows: T[],
  getPerson: (row: T) => Person | null,
  fieldType: DisplayFieldType,
) => {
  // Note: one sample per person - the same assignee on every row would just repeat itself
  const seen = new Set<string>()
  const candidates = rows
    .map((row) => ({ person: getPerson(row), row }))
    .filter((candidate): candidate is { row: T; person: Person } => !!candidate.person)
    .filter(({ person }) => {
      const key = [person.kind, person.clientId, getPersonName(person), person.email].join('|')
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })

  const wanted = [
    candidates[0],
    candidates.find(({ person }) => hasMissingDetail(t, person, fieldType)),
    fieldType === 'mixed' ? candidates.find(({ person }) => person.kind === 'resident') : undefined,
    // Note: the other kind of a Mixed column - a staff member, or a guest on Reservations
    fieldType === 'mixed' ? candidates.find(({ person }) => person.kind !== 'resident') : undefined,
    ...candidates,
  ]

  return wanted
    .filter(
      (candidate, index): candidate is { row: T; person: Person } =>
        !!candidate && wanted.indexOf(candidate) === index,
    )
    .slice(0, MAX_PREVIEW_SAMPLES)
}
